// Library Methods
import { useTranslation } from "react-i18next";
import { useState, useMemo } from "react";

// Components
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import ExamCardContainer from "../card-ui/ExamCardContainer/ExamCardContainer";
import ExamCardHeader from "../card-ui/ExamCardHeader/ExamCardHeader";
import ExamCardButton from "../card-ui/ExamCardButton/ExamCardButton";
import ExamCardToggles from "../card-ui/ExamCardToggles/ExamCardToggles";
import WhichEyeSection from "../card-ui/WhichEyeSection/WhichEyeSection";
import NumberInputField from "src/components/shared/inputs/NumberInputField/NumberInputField";

// Types
import { SelectedExamCardType } from "src/services/exams/types";

// Constants
import {
  DEFAULT_DECIBEL_VALUE_SCREENING,
  DEFAULT_VF_SCREENING_CARD,
  fixationLossMethodEnums,
  fixationPointShapeEnums,
  gridTypeEnums,
  MAX_DECIBEL_VALUE,
  MIN_DECIBEL_VALUE,
  speedTypeEnums,
  stimulusColorEnums,
  stimulusTypeEnums,
} from "src/constants/enums";

type VFScreeningCardType = {
  label: string;
  selectedExamCard: SelectedExamCardType | undefined;
  updateExamCardProperty: (
    propertyName: keyof SelectedExamCardType,
    value: SelectedExamCardType[keyof SelectedExamCardType]
  ) => void;
  onRemoveCard: () => void;
  onChangeEye: (value: string) => void;
  onChangeSectionOrder: (isRightFirst: boolean) => void;
};

const VFScreeningCard = ({
  label,
  selectedExamCard,
  updateExamCardProperty,
  onChangeEye,
  onChangeSectionOrder,
  onRemoveCard,
}: VFScreeningCardType) => {
  const { t } = useTranslation();

  const { SPHERE, DIAMOND } = fixationPointShapeEnums;
  const { EYE_TRACKING, BLIND_SPOT } = fixationLossMethodEnums;
  const { III, IV, V } = stimulusTypeEnums;
  const { NORMAL, SLOW } = speedTypeEnums;

  const examCardInfo = selectedExamCard ?? DEFAULT_VF_SCREENING_CARD;

  const {
    decibelValue: initialDecibelValue,
    visualGrid,
    eyeCheckboxes,
    sectionOrder,
    optionalToggles,
    fixationType,
    stimulusSizeType,
    stimulusColor,
    speedType,
    fovea,
  } = examCardInfo;

  const [decibelValue, setDecibelValue] = useState(
    (initialDecibelValue ?? DEFAULT_DECIBEL_VALUE_SCREENING).toString()
  );

  const vfScreenGrids = useMemo(
    () => [
      { ...gridTypeEnums.G_C_20 },
      { ...gridTypeEnums.G_C_40 },
      { ...gridTypeEnums.G_24_2 },
      { ...gridTypeEnums.G_24_2_C },
      { ...gridTypeEnums.G_30_2 },
      { ...gridTypeEnums.G_10_2 },
    ],
    []
  );

  const vfScreenStimulusSizes = useMemo(
    () => [
      { type: III, label: III },
      { type: IV, label: IV },
      { type: V, label: V },
    ],
    [III, IV, V]
  );

  const vfScreenStimulusColors = useMemo(
    () => [
      { type: stimulusColorEnums.WHITE, label: t("White") },
      { type: stimulusColorEnums.RED, label: t("Red") },
    ],
    [t]
  );

  const vfScreenFixationShapes = useMemo(
    () => [
      { type: SPHERE, label: t("Sphere") },
      { type: DIAMOND, label: t("Diamond") },
    ],
    [t, SPHERE, DIAMOND]
  );

  const vfScreenFixationMethods = useMemo(
    () => [
      { type: EYE_TRACKING, label: t("Eye Tracking") },
      { type: BLIND_SPOT, label: t("Blind Spot") },
    ],
    [t, EYE_TRACKING, BLIND_SPOT]
  );

  const vfScreenSpeedTypes = useMemo(
    () => [
      { type: NORMAL, label: t("Normal") },
      { type: SLOW, label: t("Slow") },
    ],
    [t, NORMAL, SLOW]
  );

  const vfScreenFoveaOptions = useMemo(
    () => [
      { value: true, label: t("On") },
      { value: false, label: t("Off") },
    ],
    [t]
  );

  const textStyle = {
    color: "#FFF",
    fontSize: "12px",
  };

  // A button only needs to wrap to a new line once its own label can't fit
  // -- not at some arbitrary shared width. "ch" scales with character
  // count, so each button's floor tracks its own text; +2ch covers the
  // button's horizontal padding/border.
  const buttonMinWidth = (buttonLabel: string) => `${buttonLabel.length + 2}ch`;

  return (
    <ExamCardContainer>
      <ExamCardHeader label={label} onRemoveCard={onRemoveCard} />
      <Grid
        item
        container
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        gap="8px"
        flexGrow={1}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          {/* Method */}
          <Grid
            item
            display="flex"
            flexDirection="column"
            gap="4px"
            flex="0 0 calc(50% - 4px)"
          >
            <TitleWithTooltip
              title={t("Method")}
              tooltip={t("Selects the test method.")}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />
            <ExamCardButton
              isSelected
              updateValue={() =>
                updateExamCardProperty("examType", "SCREEN_FAST")
              }
              label={t("RLFast")}
            />
          </Grid>

          {/* Stimulus Intensity */}
          <Grid
            item
            display="flex"
            flexDirection="column"
            gap="4px"
            flex="0 0 calc(50% - 4px)"
          >
            <TitleWithTooltip
              title={t("Stimulus Intensity")}
              tooltip={t("Adjusts the brightness of the stimulus.")}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />

            <Grid container item display="flex" gap="4px">
              <NumberInputField
                id="vf-screening-decibel-input"
                placeholder={t("Enter a value")}
                value={decibelValue}
                min={MIN_DECIBEL_VALUE}
                max={MAX_DECIBEL_VALUE}
                height="29px"
                onChange={(event) => {
                  setDecibelValue(event?.target?.value ?? "");
                }}
                onBlur={(finalValue) => {
                  // Invalid values reset to the minimum; decimals round to
                  // the nearest whole number -- decibel intensity is integer-only
                  const numericValue =
                    finalValue === "" || Number.isNaN(Number(finalValue))
                      ? MIN_DECIBEL_VALUE
                      : Math.round(Number(finalValue));
                  setDecibelValue(numericValue.toString());
                  updateExamCardProperty("decibelValue", numericValue);
                }}
              />
            </Grid>
          </Grid>

          {/* Grid */}
          <Grid
            item
            display="flex"
            flexDirection="column"
            gap="4px"
            flex="0 0 calc(50% - 4px)"
          >
            <TitleWithTooltip
              title={t("Grid")}
              tooltip={t("Selects the grid size.")}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />

            <Grid item display="flex" flexWrap="wrap" gap="4px">
              {vfScreenGrids.map((vfGrid, index) => (
                <Grid
                  item
                  xs
                  fontSize="12px"
                  minWidth={buttonMinWidth(vfGrid.label)}
                  key={index}
                >
                  <ExamCardButton
                    isSelected={vfGrid.type === visualGrid}
                    updateValue={() =>
                      updateExamCardProperty("visualGrid", vfGrid.type)
                    }
                    label={vfGrid.label}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Stimulus */}
          <Grid
            item
            display="flex"
            flexDirection="column"
            gap="4px"
            flex="0 0 calc(50% - 4px)"
          >
            <TitleWithTooltip
              title={t("Stimulus")}
              tooltip={t("Selects the stimulus size.")}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />

            <Grid item display="flex" flexWrap="wrap" gap="4px">
              {vfScreenStimulusSizes.map((vfStimulusSize, index) => (
                <Grid
                  item
                  xs
                  fontSize="12px"
                  minWidth={buttonMinWidth(vfStimulusSize.label)}
                  key={index}
                >
                  <ExamCardButton
                    isSelected={vfStimulusSize.type === stimulusSizeType}
                    updateValue={() =>
                      updateExamCardProperty(
                        "stimulusSizeType",
                        vfStimulusSize.type
                      )
                    }
                    label={vfStimulusSize.label}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Stimulus Color */}
          <Grid
            item
            display="flex"
            flexDirection="column"
            gap="4px"
            flex="0 0 calc(50% - 4px)"
          >
            <TitleWithTooltip
              title={t("Stimulus Color")}
              tooltip={t("Select the stimulus color.")}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />

            <Grid item display="flex" flexWrap="wrap" gap="4px">
              {vfScreenStimulusColors.map((vfStimulusColor, index) => (
                <Grid
                  item
                  xs
                  fontSize="12px"
                  minWidth={buttonMinWidth(vfStimulusColor.label)}
                  key={index}
                >
                  <ExamCardButton
                    isSelected={vfStimulusColor.type === stimulusColor}
                    updateValue={() =>
                      updateExamCardProperty(
                        "stimulusColor",
                        vfStimulusColor.type
                      )
                    }
                    label={vfStimulusColor.label}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Fixation */}
          <Grid
            item
            display="flex"
            flexDirection="column"
            gap="4px"
            flex="0 0 calc(50% - 4px)"
          >
            <TitleWithTooltip
              title={t("Fixation")}
              tooltip={t("Selects the shape of the fixation point.")}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />

            <Grid container item display="flex" flexWrap="wrap" gap="4px">
              {vfScreenFixationShapes.map((shape) => (
                <Grid
                  item
                  xs
                  fontSize="12px"
                  minWidth={buttonMinWidth(shape.label)}
                  key={shape.type}
                >
                  <ExamCardButton
                    isSelected={
                      shape.type === fixationType?.fixationPointShapeType
                    }
                    updateValue={() =>
                      updateExamCardProperty("fixationType", {
                        ...examCardInfo.fixationType,
                        fixationPointShapeType: shape.type,
                      })
                    }
                    label={shape.label}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Fixation Method */}
          <Grid
            item
            display="flex"
            flexDirection="column"
            gap="4px"
            flex="0 0 calc(50% - 4px)"
          >
            <TitleWithTooltip
              title={t("Fixation Method")}
              tooltip={t(
                "Selects which fixation method is used in the test. Either eye tracking method or Heijl-Krakau (blind spot) method."
              )}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />

            <Grid container item display="flex" flexWrap="wrap" gap="4px">
              {vfScreenFixationMethods.map((method) => (
                <Grid
                  item
                  xs
                  fontSize="12px"
                  minWidth={buttonMinWidth(method.label)}
                  key={method.type}
                >
                  <ExamCardButton
                    isSelected={
                      method.type === fixationType?.fixationLossMethod
                    }
                    updateValue={() =>
                      updateExamCardProperty("fixationType", {
                        ...examCardInfo.fixationType,
                        fixationLossMethod: method.type,
                      })
                    }
                    label={method.label}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Speed Type */}
          <Grid
            item
            display="flex"
            flexDirection="column"
            gap="4px"
            flex="0 0 calc(50% - 4px)"
          >
            <TitleWithTooltip
              title={t("Speed Type")}
              tooltip={t("Controls the speed of the exam.")}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />

            <Grid item display="flex" flexWrap="wrap" gap="4px">
              {vfScreenSpeedTypes.map((speed) => (
                <Grid
                  item
                  xs
                  fontSize="12px"
                  minWidth={buttonMinWidth(speed.label)}
                  key={speed.type}
                >
                  <ExamCardButton
                    isSelected={speed.type === speedType}
                    updateValue={() =>
                      updateExamCardProperty("speedType", speed.type)
                    }
                    label={speed.label}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Fovea Measurement */}
          <Grid
            item
            display="flex"
            flexDirection="column"
            gap="4px"
            flex="0 0 calc(50% - 4px)"
          >
            <TitleWithTooltip
              title={t("Fovea Measurement")}
              tooltip={t("Measures the patient's fovea.")}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />

            <Grid container item display="flex" flexWrap="wrap" gap="4px">
              {vfScreenFoveaOptions.map((option) => (
                <Grid
                  item
                  xs
                  fontSize="12px"
                  minWidth={buttonMinWidth(option.label)}
                  key={String(option.value)}
                >
                  <ExamCardButton
                    isSelected={option.value ? Boolean(fovea) : !fovea}
                    updateValue={() =>
                      updateExamCardProperty("fovea", option.value)
                    }
                    label={option.label}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <WhichEyeSection
            isRightFirst={sectionOrder?.right === 0}
            eyeCheckboxes={eyeCheckboxes}
            onChangeSectionOrder={onChangeSectionOrder}
            onChangeEye={onChangeEye}
          />

          <Divider color="#5B6D7A" />

          <ExamCardToggles
            optionalToggles={optionalToggles}
            updateExamCardProperty={updateExamCardProperty}
            showToggles={DEFAULT_VF_SCREENING_CARD.optionalToggles}
          />
        </Box>
      </Grid>
    </ExamCardContainer>
  );
};

export default VFScreeningCard;
